// src/components/Calendar.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import Calendar from 'react-calendar'; // Mini calendar import
import 'react-calendar/dist/Calendar.css'; // Mini calendar styles
import './Calendar.css'; // Import custom CSS

const localizer = momentLocalizer(moment);

const eventColors = {
    case_open: { name: 'Case Open', color: '#28a745' }, // Green
    case_close: { name: 'Case Close', color: '#007bff' }, // Blue
    time_entry: { name: 'Time Entry', color: '#6c757d' }, // Dark Gray
    expense_entry: { name: 'Disbursement', color: '#adb5bd' }, // Light Gray
    invoice_due: { name: 'Invoice Due', color: '#fd7e14' }, // Orange
    communication_log: { name: 'Communication', color: '#6f42c1' }, // Purple
    task_due: { name: 'Task Due', color: '#dc3545' }, // Red
};

// --- MODIFICATION: Renamed from MonthEvent to EventDot ---
// This component will be rendered for each event in MONTH view only.
// It renders a small, colored dot instead of a text block.
const EventDot = ({ event }) => {
    const typeInfo = eventColors[event.resource.type];
    const backgroundColor = typeInfo ? typeInfo.color : '#3174ad';
    
    const style = {
        backgroundColor,
        borderRadius: '50%',
        height: '10px',
        width: '10px',
        border: '1px solid white',
        display: 'inline-block', // Allows dots to flow horizontally
        margin: '1px',
        cursor: 'pointer',
    };

    // We add the title as a tooltip for accessibility/hover
    return (
        <div style={style} title={event.title}>
            {/* Intentionally empty to show just the dot */}
        </div>
    );
};

// --- NEW: WeekEvent component for week/day views ---
// Shows the event title inline with its time for proper time-slot display
const WeekEvent = ({ event }) => {
    const typeInfo = eventColors[event.resource.type];
    const color = typeInfo ? typeInfo.color : '#3174ad';
    const timeStr = event.allDay ? '' : moment(event.start).format('h:mm A');

    return (
        <div
            style={{
                fontSize: '11px',
                lineHeight: '1.3',
                padding: '2px 4px',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                color: 'white',
                cursor: 'pointer',
            }}
            title={`${event.title}${timeStr ? ' at ' + timeStr : ''}`}
        >
            {timeStr && <span style={{ fontWeight: 600, marginRight: '3px' }}>{timeStr}</span>}
            <span>{event.title}</span>
        </div>
    );
};


// This styles events in WEEK, DAY, and AGENDA views (not month)
const eventStyleGetter = (event) => {
    const typeInfo = eventColors[event.resource.type];
    const backgroundColor = typeInfo ? typeInfo.color : '#3174ad';
    return {
        style: {
            backgroundColor,
            borderRadius: '5px',
            opacity: 0.8,
            color: 'white',
            border: '0px',
            display: 'block'
        }
    };
};

// A redesigned, more modern toolbar for the main calendar
const CustomToolbar = ({ label, onNavigate, onView, views, view }) => {
    return (
        <div className="flex items-center justify-between p-3 border-b border-gray-200">
            <div className="flex items-center gap-2">
                <button type="button" className="px-3 py-1.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors" onClick={() => onNavigate('TODAY')}>Today</button>
                <div className="flex items-center">
                    <button type="button" className="p-1.5 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-full transition-colors" aria-label="Previous" onClick={() => onNavigate('PREV')}>
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
                    </button>
                    <button type="button" className="p-1.5 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-full transition-colors" aria-label="Next" onClick={() => onNavigate('NEXT')}>
                         <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
                    </button>
                </div>
            </div>
            <h2 className="text-xl font-bold text-gray-800 hidden md:block">{label}</h2>
            <div className="flex items-center space-x-1 bg-gray-100 p-1 rounded-lg">
                {views.map(viewName => (
                    <button
                        type="button"
                        key={viewName}
                        className={`px-3 py-1 text-sm rounded-md transition-colors capitalize ${view === viewName ? 'bg-white text-gray-800 shadow-sm font-semibold' : 'bg-transparent text-gray-600 hover:bg-white/50'}`}
                        onClick={() => onView(viewName)}
                    >
                        {viewName}
                    </button>
                ))}
            </div>
        </div>
    );
};

const CalendarModule = ({ authAxios, showFormMessage, navigateToItem, userRole, currentUser, cases }) => {
    // --- RBAC Permissions ---
    const isPartner = userRole === 'PARTNER' || userRole === 'SUPERUSER';
    const isSupportStaff = userRole === 'SUPPORT_STAFF';
    const isAssociate = userRole === 'ASSOCIATE';
    const isParalegal = userRole === 'PARALEGAL';
    const isBoardMember = userRole === 'BOARD_MEMBER';
    
    const [allEvents, setAllEvents] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [view, setView] = useState('month');
    const [date, setDate] = useState(new Date());
    const [searchTerm, setSearchTerm] = useState('');

    const [visibility, setVisibility] = useState(
        Object.keys(eventColors).reduce((acc, key) => ({ ...acc, [key]: true }), {})
    );

    const fetchEvents = useCallback(() => {
        authAxios.get('events/')
            .then(response => {
                const formattedEvents = response.data.map(event => ({
                    ...event,
                    start: new Date(event.start),
                    end: new Date(event.end),
                }));
                setAllEvents(formattedEvents);
            })
            .catch(error => console.error('Error fetching events:', error));
    }, [authAxios]);

    useEffect(() => {
        fetchEvents();
    }, [fetchEvents]);

    const handleVisibilityChange = (event) => {
        const { name, checked } = event.target;
        setVisibility(prev => ({ ...prev, [name]: checked }));
    };

    const handleSelectEvent = (event) => {
        setSelectedEvent(event);
        setShowModal(true);
    };
    
    // --- MODIFICATION ---
    // Added handleSelectSlot for consistency, though it's not the primary fix.
    // The main fix is the `popup` prop on BigCalendar below.
    const handleSelectSlot = useCallback(
        ({ start, end }) => {
          // You could open a create modal here, for example
          // For now, we just log it
          console.log('Slot selected:', start, end);
        },
        []
    );


    const handleCloseModal = () => {
        setShowModal(false);
        setTimeout(() => setSelectedEvent(null), 300);
    };
    
    const handleGoToItem = () => {
        if (selectedEvent) {
            navigateToItem(selectedEvent.resource);
            handleCloseModal();
        }
    };
    
    const handleEventDrop = useCallback(async ({ event, start }) => {
        if (!isPartner && !isSupportStaff) {
             showFormMessage('warning', 'You do not have permission to reschedule events.');
             return;
        }

        const { type, id } = event.resource;

        if (type !== 'task_due' && type !== 'invoice_due') {
            showFormMessage('warning', 'This event type cannot be rescheduled.');
            return;
        }

        const newDate = moment(start).toISOString();

        try {
            await authAxios.post('events/update-date/', {
                type: type,
                id: id,
                date: newDate,
            });
            showFormMessage('success', 'Event rescheduled successfully!');
            fetchEvents();
        } catch (error) {
            console.error("Error updating event date:", error);
            showFormMessage('danger', 'Failed to reschedule event.');
        }
    }, [authAxios, showFormMessage, fetchEvents, isPartner, isSupportStaff]);

    const filteredEvents = useMemo(() => {
        const lowercasedSearchTerm = searchTerm.toLowerCase();
        
        const userCaseIds = new Set();
        if ((isAssociate || isParalegal) && currentUser && cases) {
            cases.forEach(c => {
                if (c.assigned_attorney === currentUser.id || c.team_members.includes(currentUser.id)) {
                    userCaseIds.add(c.id);
                }
            });
        }

        return allEvents.filter(event => {
            const isVisibleByType = visibility[event.resource.type];
            const matchesSearch = event.title.toLowerCase().includes(lowercasedSearchTerm);

            let belongsToUserCase = true;
            if (isAssociate || isParalegal) {
                const eventCaseId = event.resource.case_id;
                belongsToUserCase = !eventCaseId || userCaseIds.has(eventCaseId);
            }
            
            return isVisibleByType && matchesSearch && belongsToUserCase;
        });
    }, [allEvents, visibility, searchTerm, isAssociate, isParalegal, currentUser, cases]);
    
    const upcomingEvents = useMemo(() => {
        const now = new Date();
        return filteredEvents
            .filter(event => event.start >= now)
            .sort((a, b) => a.start - b.start)
            .slice(0, 5); // Get the next 5 upcoming events
    }, [filteredEvents]);
    
    const handleViewChange = useCallback((newView) => setView(newView), []);
    const handleNavigate = useCallback((newDate) => setDate(newDate), []);

    const handlePrint = () => {
        window.print();
    };

    const handleExport = () => {
        const headers = ["Title", "Start Date", "End Date", "All Day", "Type"];
        const csvRows = [headers.join(',')];

        filteredEvents.forEach(event => {
            const row = [
                `"${event.title.replace(/"/g, '""')}"`,
                moment(event.start).format('YYYY-MM-DD HH:mm:ss'),
                moment(event.end).format('YYYY-MM-DD HH:mm:ss'),
                event.allDay,
                event.resource.type
            ];
            csvRows.push(row.join(','));
        });

        const csvString = csvRows.join('\n');
        const blob = new Blob([csvString], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'calendar-export.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };
    
    if (isBoardMember) {
        return (
          <div className="p-4 rounded-md bg-yellow-50 text-yellow-700">
            High-level reports are available in the Reports section. Detailed operational calendar view is not accessible for this role.
          </div>
        );
    }

    return (
        // --- FIX 1: Changed h-screen to h-full ---
        // This makes the calendar container fill its parent from App.js,
        // rather than trying to be 100% of the viewport height.
        <div className="flex h-full bg-gray-100 font-sans calendar-container">
            {/* Main Calendar View */}
            <main className="flex-1 flex flex-col p-4 gap-4">
                {/* This container is setup with flex-col, so flex-grow on the child div
                  will make it fill the available vertical space.
                */}
                <div className="flex-grow bg-white rounded-lg shadow-lg overflow-hidden calendar-component">
                    <BigCalendar
                        localizer={localizer}
                        events={filteredEvents}
                        startAccessor="start"
                        endAccessor="end"
                        // --- FIX 2: Added popup prop ---
                        // This is the key fix. It tells the calendar to show a "+N more"
                        // link and open a popup modal when clicked, instead of
                        // stretching the row to show all events.
                        popup 
                        style={{ height: '100%' }} // This is correct *now* because its parent has a defined space
                        eventPropGetter={eventStyleGetter} // Kept for Day/Agenda views
                        onSelectEvent={handleSelectEvent}
                        onSelectSlot={handleSelectSlot} // Added for completeness
                        views={['month', 'week', 'day', 'agenda']}
                        components={{
                            toolbar: CustomToolbar,
                            // Month view: compact dots for space efficiency
                            month: {
                                event: EventDot,
                            },
                            // Week view: show title + time inline for proper time-slot display
                            week: {
                                event: WeekEvent,
                            },
                        }}
                        view={view}
                        date={date}
                        onView={handleViewChange}
                        onNavigate={handleNavigate}
                        onEventDrop={handleEventDrop} 
                        resizable
                        selectable
                    />
                </div>
            </main>

            {/* Right Sidebar */}
            <aside className="w-80 flex-shrink-0 bg-gray-50 border-l border-gray-200 p-4 overflow-y-auto calendar-sidebar space-y-6">
                {/* Mini Calendar */}
                <div className="bg-white p-3 rounded-lg shadow-sm">
                    <h5 className="text-sm font-bold text-center text-gray-700 mb-2">Quick Navigate</h5>
                    <Calendar
                        onChange={handleNavigate}
                        value={date}
                        className="mini-calendar"
                    />
                </div>
                
                {/* Search */}
                 <div>
                    <input
                        type="text"
                        placeholder="Search events..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm shadow-sm"
                    />
                </div>
                
                {/* Upcoming Events - NEW FEATURE */}
                <div className="space-y-3">
                    <h3 className="text-lg font-bold text-gray-800">Upcoming</h3>
                    {upcomingEvents.length > 0 ? (
                        <ul className="space-y-3">
                            {upcomingEvents.map(event => (
                                <li key={event.resource.id + event.resource.type} className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-gray-100 cursor-pointer" onClick={() => handleSelectEvent(event)}>
                                    <div className="w-1.5 h-10 rounded-full" style={{ backgroundColor: eventColors[event.resource.type]?.color || '#3174ad' }}></div>
                                    <div>
                                        <p className="font-semibold text-sm text-gray-800">{event.title}</p>
                                        <p className="text-xs text-gray-500">
                                            {event.allDay
                                                ? moment(event.start).format('ddd, MMM D')
                                                : moment(event.start).format('ddd, MMM D, h:mm A')}
                                        </p>

                                    </div>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-sm text-gray-500 italic p-2">No upcoming events found.</p>
                    )}
                </div>

                {/* Event Type Filters */}
                <div className="space-y-3">
                    <h3 className="text-lg font-bold text-gray-800">Event Types</h3>
                    <div className="space-y-2">
                        {Object.entries(eventColors).map(([key, { name, color }]) => (
                            <div key={key} className="flex items-center">
                                <input
                                    type="checkbox"
                                    id={`check-${key}`}
                                    name={key}
                                    checked={visibility[key]}
                                    onChange={handleVisibilityChange}
                                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                />
                                <label htmlFor={`check-${key}`} className="ml-2 text-sm text-gray-600 flex items-center cursor-pointer">
                                    <span style={{ backgroundColor: color }} className="w-3 h-3 rounded-sm mr-2"></span>
                                    {name}
                                </label>
                            </div>
                        ))}
                    </div>
                </div>
                
                {/* Actions */}
                <div className="space-y-3">
                    <h3 className="text-lg font-bold text-gray-800">Actions</h3>
                    <div className="flex gap-2">
                        <button onClick={handlePrint} className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
                            Print
                        </button>
                        <button onClick={handleExport} className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                            Export
                        </button>
                    </div>
                </div>
            </aside>

            {/* Event Details Modal */}
            {selectedEvent && (
                 <div className={`fixed inset-0 bg-gray-600 bg-opacity-50 h-full w-full z-50 transition-opacity duration-300 ${showModal ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                    <div className={`relative top-0 right-0 h-full w-full max-w-md bg-white shadow-xl transform transition-transform duration-300 ease-in-out ml-auto ${showModal ? 'translate-x-0' : 'translate-x-full'}`}>
                        <div className="flex justify-between items-center p-5 border-b">
                            <h5 className="text-xl font-bold text-gray-800">Event Details</h5>
                            <button onClick={handleCloseModal} className="text-gray-500 hover:text-gray-800">
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>
                        <div className="p-6 space-y-4 overflow-y-auto" style={{ height: 'calc(100vh - 140px)' }}>
                            <h4 className="text-2xl font-semibold text-gray-900">{selectedEvent.title}</h4>
                            <div>
                                <p className="text-sm font-medium text-gray-500">Type</p>
                                <p><span className="px-2 py-1 text-xs font-semibold rounded-full text-white" style={{ backgroundColor: eventColors[selectedEvent.resource.type]?.color || '#3174ad' }}>{eventColors[selectedEvent.resource.type]?.name || 'Unknown'}</span></p>
                            </div>
                            <div>
                                <p className="text-sm font-medium text-gray-500">Date & Time</p>
                                <p className="text-gray-800">
                                    {selectedEvent.allDay
                                        ? moment(selectedEvent.start).format('MMMM Do YYYY')
                                        : moment(selectedEvent.start).format('MMMM Do YYYY, h:mm a')}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm font-medium text-gray-500">Details</p>
                                <p className="text-gray-800 bg-gray-50 p-3 rounded-md">{selectedEvent.resource.description || 'No additional details provided.'}</p>
                            </div>
                        </div>
                         <div className="flex justify-end space-x-4 p-4 border-t bg-gray-50">
                            <button type="button" onClick={handleCloseModal} className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">Close</button>
                            <button type="button" onClick={handleGoToItem} className="bg-blue-600 text-white px-4 py-2 rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">Go to Item</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default CalendarModule;


